import type { FluxDate } from './types.js';

// Annualized internal rate of return (RG-07). Contributions are outflows, the estimated
// capital is the terminal inflow. Solved by Newton with a bisection fallback, then
// annualized and rounded to 0.1 point (0.001 in decimal).

interface OptionsTri {
  readonly iterationsMax?: number;
  readonly tolerance?: number;
}

/** Net present value of the assembled cash flows at a monthly rate i. */
function van(cashflows: ReadonlyMap<number, number>, i: number): number {
  let somme = 0;
  for (const [mois, montant] of cashflows) {
    somme += montant / Math.pow(1 + i, mois);
  }
  return somme;
}

/** Derivative of the NPV with respect to i. */
function vanDerivee(cashflows: ReadonlyMap<number, number>, i: number): number {
  let somme = 0;
  for (const [mois, montant] of cashflows) {
    somme += (-mois * montant) / Math.pow(1 + i, mois + 1);
  }
  return somme;
}

/**
 * Assemble the monthly cash flows: each validated contribution is an outflow at its
 * month, the terminal value is an inflow at the terminal month.
 */
function assembler(
  flux: readonly FluxDate[],
  valeurTerminale: number,
  moisTerminal: number,
): Map<number, number> {
  const cashflows = new Map<number, number>();
  for (const f of flux) {
    cashflows.set(f.mois, (cashflows.get(f.mois) ?? 0) - f.montant);
  }
  cashflows.set(moisTerminal, (cashflows.get(moisTerminal) ?? 0) + valeurTerminale);
  return cashflows;
}

/** Monthly IRR, or null when it cannot be resolved. */
export function triMensuel(
  flux: readonly FluxDate[],
  valeurTerminale: number,
  moisTerminal: number,
  options: OptionsTri = {},
): number | null {
  const totalVerse = flux.reduce((s, f) => s + f.montant, 0);
  if (totalVerse <= 0 || moisTerminal <= 0) {
    return null;
  }
  const cashflows = assembler(flux, valeurTerminale, moisTerminal);
  // No IRR without a sign change in the cash flows (Descartes): a near-total loss where
  // every net flow stays negative has no meaningful rate.
  const valeurs = [...cashflows.values()];
  const aPositif = valeurs.some((v) => v > 0);
  const aNegatif = valeurs.some((v) => v < 0);
  if (!aPositif || !aNegatif) {
    return null;
  }
  const iterationsMax = options.iterationsMax ?? 100;
  const tolerance = options.tolerance ?? 1e-10;

  // Newton.
  let i = 0.01;
  for (let k = 0; k < iterationsMax; k++) {
    const f = van(cashflows, i);
    if (Math.abs(f) < tolerance) {
      return i > -1 ? i : null;
    }
    const df = vanDerivee(cashflows, i);
    if (df === 0 || !Number.isFinite(df)) {
      break;
    }
    const prochain = i - f / df;
    if (!Number.isFinite(prochain) || prochain <= -0.9999) {
      break;
    }
    if (Math.abs(prochain - i) < tolerance) {
      return prochain;
    }
    i = prochain;
  }

  return bissection(cashflows, tolerance, iterationsMax);
}

/** Bisection fallback over a wide monthly-rate bracket. */
function bissection(
  cashflows: ReadonlyMap<number, number>,
  tolerance: number,
  iterationsMax: number,
): number | null {
  let lo = -0.9999;
  let hi = 10;
  let flo = van(cashflows, lo);
  let fhi = van(cashflows, hi);
  if (flo === 0) return lo;
  if (fhi === 0) return hi;
  if (flo * fhi > 0) {
    return null;
  }
  for (let k = 0; k < iterationsMax * 4; k++) {
    const mid = (lo + hi) / 2;
    const fmid = van(cashflows, mid);
    if (Math.abs(fmid) < tolerance || (hi - lo) / 2 < tolerance) {
      return mid;
    }
    if (flo * fmid < 0) {
      hi = mid;
      fhi = fmid;
    } else {
      lo = mid;
      flo = fmid;
    }
  }
  return (lo + hi) / 2;
}

/**
 * Annualized IRR rounded to 0.1 point, or null when not solvable.
 * @returns decimal rate (e.g. 0.031 for 3.1%).
 */
export function triAnnualise(
  flux: readonly FluxDate[],
  valeurTerminale: number,
  moisTerminal: number,
  options: OptionsTri = {},
): number | null {
  const mensuel = triMensuel(flux, valeurTerminale, moisTerminal, options);
  if (mensuel === null) {
    return null;
  }
  const annuel = Math.pow(1 + mensuel, 12) - 1;
  return Math.round(annuel * 1000) / 1000;
}
